// Console authentication seam (client side).
//
// This is the SPA counterpart to service/src/admin/auth.ts. It is the single
// place the console decides "who am I and may I be here".
//
// TWO MODES, chosen by whether the Keycloak client is configured (`VITE_KC_*`):
//   • REAL (issuer configured): a Keycloak Authorization-Code + PKCE sign-in
//     (see ./oidc). We acquire an access token, attach it as a `Bearer` via
//     `setIdentityHeaders`, then load the admin context — the service verifies
//     the token and requires the `iam.admin` scope (a 401/403 lands us in the
//     error state). A missing/expired token redirects to Keycloak.
//   • DEV-STUB (issuer absent): attach the dev identity header the service stub
//     honours, so the console stays usable locally without Keycloak.
// NOTHING else in the app changes: components only read `useAdminAuth()`.

import { type ReactNode, createContext, useContext, useEffect, useState } from "react"
import { setIdentityHeaders } from "../api/client.js"
import { adminApi } from "../api/endpoints.js"
import type { AdminContext as AdminContextDto } from "../api/types.js"
import {
  type OidcConfig,
  beginLogin,
  completeLogin,
  getAccessToken,
  getOidcConfig,
  isRedirectCallback
} from "./oidc.js"

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; context: AdminContextDto }
  | { status: "error"; message: string }

const AuthContext = createContext<AuthState>({ status: "loading" })

/** DEV-STUB: attach the dev identity header the service stub trusts. */
function bootstrapStubIdentity(): void {
  const params = new URLSearchParams(window.location.search)
  const user = params.get("as") ?? "dev-admin"
  setIdentityHeaders({ "x-admin-user": user })
}

/**
 * REAL: ensure we hold a valid access token and attach it as a bearer. Returns
 * true when authenticated; returns false only while redirecting to Keycloak (the
 * page is navigating away, so the caller should stop). Throws on a failed code
 * exchange — the caller surfaces it as an auth error.
 */
async function bootstrapKeycloakIdentity(cfg: OidcConfig): Promise<boolean> {
  // 1) Returning from Keycloak with an authorization code → exchange it.
  if (isRedirectCallback()) {
    const tokens = await completeLogin(cfg)
    setIdentityHeaders({ authorization: `Bearer ${tokens.accessToken}` })
    return true
  }
  // 2) Already signed in (valid or refreshable token) → use it.
  const token = await getAccessToken(cfg)
  if (token) {
    setIdentityHeaders({ authorization: `Bearer ${token}` })
    return true
  }
  // 3) No token → redirect to Keycloak (never returns).
  await beginLogin(cfg)
  return false
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" })

  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      const cfg = getOidcConfig()
      if (cfg) {
        const ready = await bootstrapKeycloakIdentity(cfg)
        if (!ready) return // redirecting to Keycloak — stop here.
      } else {
        bootstrapStubIdentity()
      }
      const context = await adminApi.context()
      if (!cancelled) setState({ status: "authenticated", context })
    }

    bootstrap().catch((err: unknown) => {
      if (!cancelled) {
        const message = err instanceof Error ? err.message : "authentification échouée"
        setState({ status: "error", message })
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

/** Read the current console-auth state. */
export function useAdminAuth(): AuthState {
  return useContext(AuthContext)
}

/** Convenience: the resolved context when authenticated, else null. */
export function useAdminContext(): AdminContextDto | null {
  const state = useAdminAuth()
  return state.status === "authenticated" ? state.context : null
}

/** Convenience: whether write actions are live (drives disabled UI + banner). */
export function useWritesEnabled(): boolean {
  const ctx = useAdminContext()
  return ctx?.writesEnabled ?? false
}
